import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './exhibitors.css';
import heroImg from '../assets/heroimg.png';
import exhibitorsImg from '../assets/exhibitors.jpg';
import arrowIcon from '../assets/arrow.png';

gsap.registerPlugin(ScrollTrigger);

const exhibitors = [
  { initials: 'CTI', name: 'Cairo Textile Industries', category: 'Manufacturing', hall: 'Hall A', featured: true, text: 'Integrated garment and textile production solutions.' },
  { initials: 'DMC', name: 'Delta Machinery Co.', category: 'Equipment', hall: 'Hall B', text: 'Cutting, sewing, and finishing machinery.' },
  { initials: 'AG', name: 'Alexandria Garments', category: 'Textiles', hall: 'Hall C', text: 'Fabric suppliers and garment production partners.' },
  { initials: 'MTS', name: 'Modern Tech Solutions', category: 'Automation', hall: 'Hall A', featured: true, text: 'Smart factory tools and production software.' },
  { initials: 'NIS', name: 'Nile Industrial Systems', category: 'Manufacturing', hall: 'Hall B', text: 'Industrial systems for high-volume factories.' },
  { initials: 'SEL', name: 'Suez Equipment Ltd.', category: 'Equipment', hall: 'Hall C', text: 'Machinery support, spare parts, and installation.' },
  { initials: 'DTC', name: 'Dubai Textile Group', category: 'Textiles', hall: 'Hall A', text: 'Regional textile sourcing and distribution.' },
  { initials: 'SIC', name: 'Saudi Industrial Corp.', category: 'Manufacturing', hall: 'Hall B', featured: true, text: 'Production lines and regional manufacturing services.' },
  { initials: 'DMC', name: 'Delta Machinery Co. Premium', category: 'Equipment', hall: 'Hall C', text: 'Advanced finishing and inspection machinery.' },
];

const categories = ['All', 'Manufacturing', 'Equipment', 'Textiles', 'Automation'];

const Exhibitors = () => {
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [saved, setSaved] = useState([]);
  const [activeExhibitor, setActiveExhibitor] = useState(exhibitors[0]);

  const visibleExhibitors = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return exhibitors.filter((exhibitor) => {
      const matchesCategory = activeCategory === 'All' || exhibitor.category === activeCategory;
      const matchesSearch = !normalized || `${exhibitor.name} ${exhibitor.category} ${exhibitor.hall}`.toLowerCase().includes(normalized);
      return matchesCategory && matchesSearch;
    });
  }, [query, activeCategory]);

  useEffect(() => {
    setActiveExhibitor(visibleExhibitors[0] || exhibitors[0]);
  }, [visibleExhibitors]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.exhibitors-hero-line', {
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 1.1,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.from('.exhibitors-hero-item', {
        y: 54,
        opacity: 0,
        filter: 'blur(10px)',
        duration: 0.95,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.to('.exhibitors-orbit', {
        rotate: 360,
        duration: 28,
        repeat: -1,
        ease: 'none',
      });

      gsap.utils.toArray('[data-exhibitors-reveal]').forEach((item) => {
        gsap.fromTo(
          item,
          { y: 58, filter: 'blur(8px)' },
          {
            y: 0,
            filter: 'blur(0px)',
            duration: 0.85,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: item,
              start: 'top 86%',
              once: true,
            },
          }
        );
      });
    }, page);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const cards = pageRef.current?.querySelectorAll('.exhibitor-spotlight');
    if (!cards) return undefined;

    const moveSpotlight = (event) => {
      const card = event.currentTarget;
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
      card.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
    };

    cards.forEach((card) => card.addEventListener('mousemove', moveSpotlight));
    return () => cards.forEach((card) => card.removeEventListener('mousemove', moveSpotlight));
  }, [visibleExhibitors]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const moveHero = (event) => {
      const rect = hero.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      hero.style.setProperty('--mouse-x', `${x}px`);
      hero.style.setProperty('--mouse-y', `${y}px`);
      gsap.to('.exhibitors-hero-img', {
        x: (x / rect.width - 0.5) * 24,
        y: (y / rect.height - 0.5) * 18,
        duration: 0.8,
        ease: 'power3.out',
      });
    };

    window.addEventListener('mousemove', moveHero);
    return () => window.removeEventListener('mousemove', moveHero);
  }, []);

  return (
    <main className="exhibitors-page" ref={pageRef}>
      <section className="exhibitors-hero" ref={heroRef}>
        <div className="exhibitors-hero-media">
          <img src={heroImg} alt="Exhibition hall with textile machinery booths" className="exhibitors-hero-img" />
        </div>
        <div className="exhibitors-hero-overlay" />
        <span className="exhibitors-hero-line exhibitors-line-two" />

        <div className="exhibitors-hero-content">
          <span className="exhibitors-badge exhibitors-hero-item">342 exhibitors / 45 countries</span>
          <h1 className="exhibitors-hero-item">Discover Innovation. Connect with Leaders.</h1>
          <p className="exhibitors-hero-item">
            Explore manufacturers, machinery suppliers, textile innovators, and automation
            partners shaping the future of garment production.
          </p>
        </div>

        <aside className="exhibitors-hero-card exhibitor-spotlight exhibitors-hero-item">
          <div className="exhibitors-orbit" aria-hidden="true" />
          <span>Directory</span>
          <strong>{visibleExhibitors.length}</strong>
          <p>Matching exhibitors</p>
        </aside>
      </section>

      <section className="exhibitor-directory" data-exhibitors-reveal>
        <form className="exhibitor-search" role="search" onSubmit={(event) => event.preventDefault()}>
          <label htmlFor="exhibitor-search">Search exhibitors by name, hall, or category</label>
          <input
            id="exhibitor-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search exhibitors by name or industry..."
          />
        </form>

        <div className="exhibitor-filters" aria-label="Filter exhibitors by category">
          {categories.map((category) => (
            <button
              type="button"
              key={category}
              className={activeCategory === category ? 'exhibitor-filter is-active' : 'exhibitor-filter'}
              aria-pressed={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <section className="exhibitor-feature exhibitor-spotlight" aria-live="polite">
          <div>
            <span className="exhibitors-badge">Selected Exhibitor</span>
            <h2>{activeExhibitor.name}</h2>
            <p>{activeExhibitor.text}</p>
          </div>
          <div className="exhibitor-feature-meta">
            <strong>{activeExhibitor.initials}</strong>
            <span>{activeExhibitor.category}</span>
            <small>{activeExhibitor.hall}</small>
          </div>
        </section>

        <div className="exhibitor-grid" aria-live="polite">
          {visibleExhibitors.map((exhibitor, index) => {
            const isSaved = saved.includes(exhibitor.name);
            return (
              <article
                className={activeExhibitor.name === exhibitor.name ? 'directory-card directory-card-active exhibitor-spotlight' : 'directory-card exhibitor-spotlight'}
                key={exhibitor.name}
                style={{ '--delay': `${index * 70}ms` }}
              >
                {exhibitor.featured && <span className="directory-featured">Featured</span>}
                <div className="directory-mark">{exhibitor.initials}</div>
                <div>
                  <h2>{exhibitor.name}</h2>
                  <p>{exhibitor.category} / {exhibitor.hall}</p>
                  <small>{exhibitor.text}</small>
                </div>
                <div className="directory-actions">
                  <button
                    type="button"
                    className={isSaved ? 'directory-save is-saved' : 'directory-save'}
                    aria-pressed={isSaved}
                    onClick={() => {
                      setSaved((current) => (
                        current.includes(exhibitor.name)
                          ? current.filter((item) => item !== exhibitor.name)
                          : [...current, exhibitor.name]
                      ));
                    }}
                  >
                    {isSaved ? 'Saved' : 'Save'}
                  </button>
                  <button type="button" className="directory-view" onClick={() => setActiveExhibitor(exhibitor)}>View Details</button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="exhibitors-image-band" data-exhibitors-reveal>
        <div className="exhibitors-image-copy">
          <span className="exhibitors-badge">Exhibition Floor</span>
          <h2>See the scale of the industry in one connected venue.</h2>
          <p>Use the directory above to plan booth visits, save important suppliers, and move through the exhibition with purpose.</p>
        </div>
        <div className="exhibitors-parallax-image" aria-label="Exhibitors and visitors inside the exhibition hall">
          <img src={exhibitorsImg} alt="Exhibitors and visitors inside the textile machinery exhibition hall" />
        </div>
      </section>

      <section className="exhibitor-cta exhibitor-spotlight" data-exhibitors-reveal>
        <div>
          <span className="exhibitors-badge">Become an Exhibitor</span>
          <h2>Showcase your innovation to textile industry professionals.</h2>
          <p>Secure visibility, meet qualified buyers, and position your brand in Egypts leading garment and textile machinery exhibition.</p>
        </div>
        <Link to="/tickets" className="exhibitor-cta-btn">
          Register Interest
          <img src={arrowIcon} alt="" aria-hidden="true" />
        </Link>
      </section>
    </main>
  );
};

export default Exhibitors;
